import { AlertCircle, Calendar, CheckCircle, Clock, Settings } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyStateWithError, LoadingState, QueryErrorDisplay } from "@/lib/components/errors";
import { BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { transformApolloError } from "@/lib/error/error-utils";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useSalonStore } from "@/lib/store/salon";
import { PermissionDebugCard, WorkerAppointmentsList, WorkerScheduleHeader } from "../components";
import {
  useCurrentWorker,
  useWorkerCompletedAppointments,
  useWorkerTodayAppointments,
  useWorkerUpcomingAppointments,
} from "../hooks";
import type { WorkerTabType } from "../types";

export function DashboardWorkerPage() {
  const [activeTab, setActiveTab] = useState<WorkerTabType>("today");
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const { currentWorker, isWorker, loading: workerLoading } = useCurrentWorker();
  const { user } = useAuthStore();
  const { currentSalon } = useSalonStore();

  // Debug: Get current user salon role
  const currentUserSalonRole = currentSalon ? { role: currentSalon.role } : null;

  // If user is not a worker, show appropriate message with debug info
  if (!workerLoading && !isWorker) {
    return (
      <PageContent>
        <div className="flex items-center justify-center h-64">
          <div className="text-center max-w-md">
            <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">Worker Access Required</h3>
            <p className="text-muted-foreground mb-4">
              This page is only available to workers. Please contact your administrator if you
              believe this is an error.
            </p>

            {/* Debug Information */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDebugInfo(!showDebugInfo)}
              className="mb-4"
            >
              <Settings className="w-4 h-4 mr-2" />
              {showDebugInfo ? "Hide" : "Show"} Debug Info
            </Button>

            {showDebugInfo && (
              <Card className="text-left">
                <CardHeader>
                  <CardTitle className="text-sm">Debug Information</CardTitle>
                </CardHeader>
                <CardContent className="text-xs space-y-2">
                  <div>
                    <strong>User ID:</strong> {user?.id}
                  </div>
                  <div>
                    <strong>User Email:</strong> {user?.email}
                  </div>
                  <div>
                    <strong>Current Salon:</strong> {currentSalon?.name} ({currentSalon?.id})
                  </div>
                  <div>
                    <strong>User Salon Role:</strong> {JSON.stringify(currentUserSalonRole?.role)}
                  </div>
                  <div>
                    <strong>Is Worker (Hook):</strong> {isWorker ? "Yes" : "No"}
                  </div>
                  <div>
                    <strong>Current Worker:</strong> {currentWorker ? currentWorker.id : "None"}
                  </div>
                  <div>
                    <strong>Current Salon Role:</strong>{" "}
                    {JSON.stringify(currentSalon?.role, null, 2)}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </PageContent>
    );
  }

  const workerName = currentWorker
    ? `${currentWorker.user?.firstName || ""} ${currentWorker.user?.lastName || ""}`.trim()
    : "";

  return (
    <>
      {/* Single Navigation Bar for both desktop and mobile */}
      <BaseNavigationBar
        title="My Schedule"
        subtitle={workerName ? `Welcome back, ${workerName}` : "Worker Dashboard"}
      />

      <PageContent>
        {/* Schedule Header - Shows current time, next appointment, daily stats */}
        <WorkerScheduleHeader />

        {/* Tabs for different appointment views */}
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as WorkerTabType)}>
          {/* Tab Navigation */}
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="today" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span className="hidden sm:inline">Today</span>
            </TabsTrigger>
            <TabsTrigger value="upcoming" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">Upcoming</span>
            </TabsTrigger>
            <TabsTrigger value="completed" className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Completed</span>
            </TabsTrigger>
          </TabsList>

          {/* Today's Appointments */}
          <TabsContent value="today" forceMount className={activeTab !== "today" ? "hidden" : ""}>
            <TodayAppointmentsTab />
          </TabsContent>

          {/* Upcoming Appointments */}
          <TabsContent
            value="upcoming"
            forceMount
            className={activeTab !== "upcoming" ? "hidden" : ""}
          >
            <UpcomingAppointmentsTab />
          </TabsContent>

          {/* Completed Appointments */}
          <TabsContent
            value="completed"
            forceMount
            className={activeTab !== "completed" ? "hidden" : ""}
          >
            <CompletedAppointmentsTab />
          </TabsContent>
        </Tabs>
      </PageContent>
    </>
  );
}

// Today's Appointments Tab Component
function TodayAppointmentsTab() {
  const { appointments, loading, error, retry } = useWorkerTodayAppointments();

  if (loading && appointments.length === 0) {
    return <LoadingState message="Loading today's appointments..." size="lg" />;
  }

  if (error && appointments.length === 0) {
    const standardError = transformApolloError(error);
    return (
      <div className="space-y-4">
        <QueryErrorDisplay error={standardError} onRetry={retry} size="lg" />
        <PermissionDebugCard error={error} title="Appointment Access Error" />
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <EmptyStateWithError
        title="No appointments today"
        description="You have a clear schedule for today. Enjoy your free time!"
        error={null}
      />
    );
  }

  return <WorkerAppointmentsList appointments={appointments} showQuickActions={true} />;
}

// Upcoming Appointments Tab Component
function UpcomingAppointmentsTab() {
  const { appointments, loading, error, retry } = useWorkerUpcomingAppointments();

  if (loading && appointments.length === 0) {
    return <LoadingState message="Loading upcoming appointments..." size="lg" />;
  }

  if (error && appointments.length === 0) {
    const standardError = transformApolloError(error);
    return (
      <div className="space-y-4">
        <QueryErrorDisplay error={standardError} onRetry={retry} size="lg" />
        <PermissionDebugCard error={error} title="Upcoming Appointments Access Error" />
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <EmptyStateWithError
        title="No upcoming appointments"
        description="Your schedule is clear for the coming days."
        error={null}
      />
    );
  }

  return <WorkerAppointmentsList appointments={appointments} showQuickActions={false} />;
}

// Completed Appointments Tab Component
function CompletedAppointmentsTab() {
  const { appointments, loading, error, retry } = useWorkerCompletedAppointments();

  if (loading && appointments.length === 0) {
    return <LoadingState message="Loading completed appointments..." size="lg" />;
  }

  if (error && appointments.length === 0) {
    return <QueryErrorDisplay error={error} onRetry={retry} size="lg" />;
  }

  if (appointments.length === 0) {
    return (
      <EmptyStateWithError
        title="No completed appointments"
        description="Completed appointments will appear here."
        error={null}
      />
    );
  }

  return <WorkerAppointmentsList appointments={appointments} showQuickActions={false} />;
}
